import React, { useState } from 'react';
import Modal from 'react-modal';
import { IoClose } from 'react-icons/io5';
import { uploadDataFile } from '../../api/api';
import { toast } from 'react-toastify';
import './UploadModal.css';

const Req = () => <span className="req" aria-hidden="true">*</span>;

const UploadModal = ({ isOpen, onRequestClose }) => {
  const [isTest, setIsTest] = useState(false);
  const [type, setType] = useState('solar');
  const [makeAndModel, setMakeAndModel] = useState('');
  const [location, setLocation] = useState('');
  const [subLocation, setSubLocation] = useState('');
  const [poe, setPoe] = useState('');
  const [maxRange, setMaxRange] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [interval, setInterval] = useState('15 mins');
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    resetForm();
    onRequestClose();
  };

  const resetForm = () => {
    setIsTest(false);
    setType('solar');
    setMakeAndModel('');
    setLocation('');
    setSubLocation('');
    setPoe('');
    setMaxRange('');
    setStartDate('');
    setEndDate('');
    setInterval('15 mins');
    setFile(null);
    setErrors({});
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!type.trim()) newErrors.type = 'Type is required';
    if (!makeAndModel.trim()) newErrors.makeAndModel = 'Make and model is required';
    if (!location.trim()) newErrors.location = 'Location is required';
    else if (location.trim().startsWith('Test_')) {
      newErrors.location =
        "Location must not start with 'Test_'. Use the Test / Actual tabs above instead.";
    }
    if (!subLocation.trim()) newErrors.subLocation = 'Sub-location is required';
    if (!poe.trim()) newErrors.poe = 'PoE is required';
    if (!maxRange.trim()) newErrors.maxRange = 'Max range is required';
    if (!startDate.trim()) newErrors.startDate = 'Start date is required';
    if (!endDate.trim()) newErrors.endDate = 'End date is required';
    if (!interval.trim()) newErrors.interval = 'Interval is required';
    if (!file) newErrors.file = 'File is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields', {
        position: 'bottom-right',
        autoClose: 3000,
      });
      return;
    }

    setIsUploading(true);

    try {
      await uploadDataFile(
        type,
        makeAndModel,
        location,
        maxRange,
        startDate,
        endDate,
        interval,
        file,
        subLocation,
        poe,
        isTest
      );

      handleClose();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const isFormValid = () => {
    return (
      type.trim() &&
      makeAndModel.trim() &&
      location.trim() &&
      subLocation.trim() &&
      poe.trim() &&
      maxRange.trim() &&
      startDate.trim() &&
      endDate.trim() &&
      interval.trim() &&
      file
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Upload Data File"
      className="modal upload-modal"
      overlayClassName="overlay"
    >
      <div className="modal-header">
        <div>
          <h2>Upload Generation File</h2>
          <p className="modal-header-subtitle">
            Choose Test or Actual, then fill in the profile details
          </p>
        </div>
        <IoClose className="close-icon" onClick={handleClose} aria-label="Close" />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="modal-content">
          <div className="upload-data-mode" role="group" aria-label="Data source">
            <button
              type="button"
              className={`upload-data-mode-btn ${isTest ? 'active' : ''}`}
              onClick={() => setIsTest(true)}
              disabled={isUploading}
            >
              Test
            </button>
            <button
              type="button"
              className={`upload-data-mode-btn ${!isTest ? 'active' : ''}`}
              onClick={() => setIsTest(false)}
              disabled={isUploading}
            >
              Actual
            </button>
          </div>

          <section className="form-section">
            <h3 className="form-section-title">Profile</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="type">Type <Req /></label>
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={errors.type ? 'error' : ''}
                >
                  <option value="solar">Solar</option>
                  <option value="wind">Wind</option>
                </select>
                {errors.type && <div className="error-message">{errors.type}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="location">Location <Req /></label>
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className={errors.location ? 'error' : ''}
                  placeholder="e.g., California"
                />
                {errors.location && <div className="error-message">{errors.location}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="subLocation">Sub-location <Req /></label>
                <input
                  type="text"
                  id="subLocation"
                  value={subLocation}
                  onChange={(e) => setSubLocation(e.target.value)}
                  className={errors.subLocation ? 'error' : ''}
                  placeholder="e.g., Site A / Block 1"
                />
                {errors.subLocation && <div className="error-message">{errors.subLocation}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="poe">PoE <Req /></label>
                <input
                  type="text"
                  id="poe"
                  value={poe}
                  onChange={(e) => setPoe(e.target.value)}
                  className={errors.poe ? 'error' : ''}
                  placeholder="e.g., P90, P75"
                />
                {errors.poe && <div className="error-message">{errors.poe}</div>}
              </div>

              <div className="form-group span-2">
                <label htmlFor="makeAndModel">Make and Model <Req /></label>
                <input
                  type="text"
                  id="makeAndModel"
                  value={makeAndModel}
                  onChange={(e) => setMakeAndModel(e.target.value)}
                  className={errors.makeAndModel ? 'error' : ''}
                  placeholder="e.g., SunPower X22-370"
                />
                {errors.makeAndModel && <div className="error-message">{errors.makeAndModel}</div>}
              </div>
            </div>
          </section>

          <section className="form-section">
            <h3 className="form-section-title">Time range</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="maxRange">Max Range <Req /></label>
                <select
                  id="maxRange"
                  value={maxRange}
                  onChange={(e) => setMaxRange(e.target.value)}
                  className={errors.maxRange ? 'error' : ''}
                >
                  <option value="">Select a range</option>
                  <option value="1yr">1 year</option>
                  <option value="6mo">6 months</option>
                  <option value="3mo">3 months</option>
                  <option value="1mo">1 month</option>
                </select>
                {errors.maxRange && <div className="error-message">{errors.maxRange}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="interval">Interval <Req /></label>
                <select
                  id="interval"
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                  className={errors.interval ? 'error' : ''}
                >
                  <option value="15 mins">15 mins</option>
                  <option value="1 hr">1 hr</option>
                </select>
                {errors.interval && <div className="error-message">{errors.interval}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="startDate">Start Date <Req /></label>
                <input
                  type="text"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={errors.startDate ? 'error' : ''}
                  placeholder="DD/MM/YYYY"
                />
                {errors.startDate && <div className="error-message">{errors.startDate}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date <Req /></label>
                <input
                  type="text"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={errors.endDate ? 'error' : ''}
                  placeholder="DD/MM/YYYY"
                />
                {errors.endDate && <div className="error-message">{errors.endDate}</div>}
              </div>
            </div>
          </section>

          <section className="form-section">
            <h3 className="form-section-title">Data file</h3>
            <div
              className={`file-drop${file ? ' has-file' : ''}${errors.file ? ' has-error' : ''}`}
            >
              <input
                type="file"
                id="file"
                onChange={handleFileChange}
                accept=".csv"
                aria-label="Data file CSV"
              />
              {file ? (
                <>
                  <div className="file-drop-title">CSV selected</div>
                  <div className="file-info">{file.name}</div>
                  <div className="file-drop-hint">Click to choose a different file</div>
                </>
              ) : (
                <>
                  <div className="file-drop-title">Drop a CSV here, or click to browse</div>
                  <div className="file-drop-hint">Accepted format: .csv</div>
                </>
              )}
            </div>
            {errors.file && <div className="error-message">{errors.file}</div>}
          </section>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="upload-button"
            disabled={!isFormValid() || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UploadModal;
